/*
********************************************************************************
ENTRY POINT JS FILE: Class definitions for page components.  NOTE: service 
worker registration moved to a module script block in index.html.

WORKING WITH THIS APP: DEVELOPMENT VS PRODUCTION

To simulate app data flow, index.html loads data from a JSON file via 
dummy-data-loader.js on app initialization.  The NAVS API sets a flag (liveData 
= true), in which case we use data passed from NAVS.  The app currently supports
two different inspection direction flows: one for delivery (AKA outgoing), one for 
return (incoming). To simulate either flow, update the JSON files for appropriate vendor 
(Ford vs. Mercedes-Benz) by changing navsData.direction to 'delivery' or 'return' as 
required.  To change vendors, update the app.settings.dummyVendor key.  

*** SOURCE OF TRUTH ***
***DELIVERY=OUTGOING, RETURN=INCOMING***
********************************************************************************
*/

//@ts-nocheck
// Toggle preceding from @ts-nocheck to @ts-check to enable TypeScript checking

import "@ionic/pwa-elements";
import "@ionic/core/loader";
import "@ionic/core/components";
import "./shared/ionic-imports.js";
import { MarkerManager } from "./classes/marker-manager.js";
//prettier-ignore
import { 
   consoleMsg, 
   attachRefresherEvent, 
   handleRefresh,
   updateFavicon,
   setMenu, 
   setInspector, 
   updateOfflineStatus, 
   selectRadios, 
   populateTable,
   titleCase,
   isLiveData,
   logDataMode
} from "./shared/utility.js";
import { initializePage, loadHTML, loadPages, inspectionPages, loadFirstPage, mkAbs } from "./shared/page-init.js";
// prettier-ignore
import { viewPhoto } from "./shared/capacitor-utils.js";
import { generateInspectionPDF } from "./shared/pdf-main.js";
// prettier-ignore
import { 
   isLocalStorageAvailable, 
   storeTableData, 
   clearStorageOnStartup, 
   restorePhotoData,
   clearStorage 
} from "./shared/storage.js";
// prettier-ignore
import {
   next,
   previous,
   restoreFromLocalStorage,
   showDiv,
   hideDiv,
   showTextArea,
   wireIssueTextAlert,
   buildSubmissionData,
   navsSubmit,
   handleSubmit,
   displaySubmissionMessage,
   prefillVendorDates,
} from "./shared/navigation.js";
import { validatePage } from "./shared/validation.js";
import { restoreDamageMarkers, mapClicked, resetDialogState, storeDamageMarkers } from "./shared/damage-map.js";
// prettier-ignore
import { 
   combinedVehicleDetails, 
   updateCombinedVehicleDetails, 
   devModePhotosValidation, 
   updateDeliveryNotes,
   initializeAppData
 } from "./shared/app-data.js";

/* Importing SignaturePad from node_modules, and required functions from signature-functions.js
(previously app.js)
*/
import SignaturePad from "signature_pad";
import { resizeCanvas, download, dataURLToBlob } from "./shared/signature-functions.js";

// Import required CSS files
import "../css/main.css";
//import "../css/dark.class.css";
import "../css/signature-pad.css";

import { defineCustomElements } from "@ionic/pwa-elements/loader";
import { PhotoManager } from "./classes/photo-manager";
import { DamagePhotoManager } from "./classes/damage-photo-manager";
import { InspectionPhotoManager } from "./classes/inspection-photo-manager.js";
import { PageFlowManager } from "./classes/PageFlowManager.js";

// Page class imports
import { Details } from "./pages/details.js";
import { Photos } from "./pages/photos.js";
import { Exterior } from "./pages/exterior.js";
import { Glovebox } from "./pages/glovebox.js";
import { Interior } from "./pages/interior.js";
import { RoadTest } from "./pages/road-test.js";
import { Lights } from "./pages/lights.js";
import { Fluids } from "./pages/fluids.js";
import { Tires } from "./pages/tires.js";
import { Signature } from "./pages/signature.js";
import { AppSettings } from "./pages/app-settings.js";
import { MbInterior } from "./pages/mb-interior.js";
import { MbRoad } from "./pages/mb-inspection-road.js";
import { MbFunctions } from "./pages/mb-functions.js";
import { MbCommunication } from "./pages/mb-communication.js";
import { MbOutVehicleSettings } from "./pages/mb-out-vehicle-settings.js";
import { MbOutDisplay } from "./pages/mb-out-display.js";
import { MbOutUponDelivery } from "./pages/mb-out-upon-delivery.js";
import { SubmissionComplete } from "./pages/submission-complete.js";

//  Service Worker registration: dynamic import for production environment
if ("serviceWorker" in navigator) {
	// Check Vite built-in constant to determine if running prod or dev
	if (import.meta.env.PROD) {
		// Register only in production builds (e.g., Vite Preview or NAVS)
		import("./register-sw.js")
			.then(async () => {
				await navigator.serviceWorker.ready;
				consoleMsg("✅ SW is ready — loading first page...");
			})
			.catch((err) => console.warn("SW registration failed:", err));
	} else {
		navigator.serviceWorker.getRegistrations().then((regs) => {
			for (const reg of regs) reg.unregister();
		});
		consoleMsg("⚙️ Dev mode — service worker disabled, loading immediately...");
	}
}

// Make sure our app references are using the global app object associated with window
const app = window.app;

/*
dummy-data-loader.js (local/dev index.html only) populates app.data from an async fetch that
nothing used to wait for. When that fetch was slow — e.g. a cold cache right after a service
worker update — the code below ran first with app.data.dataTables still empty. That leaves
app.settings.vendor undefined, which shows as "undefined" in the header and, worse, silently
builds a default-flow PageFlowManager for a Mercedes inspection.
Production (index.php) injects app.data synchronously and never defines this promise, so this
is a no-op there.
*/
if (app.dummyDataReady) await app.dummyDataReady;

/*
NAVS index.php injects app.data.navsData and sets app.settings.liveData from a
DOMContentLoaded handler, but this module is deferred and therefore runs BEFORE
DOMContentLoaded fires. Reading app.settings.liveData here without waiting always
saw the globals.js default (false), so clearStorageOnStartup() returned on its
first line and never ran in production. Wait for the document to finish parsing so
the live flag and navsData are actually present. No-op locally: dummy-data-loader
sets its values before this point and readyState is already past "loading".
*/
if (document.readyState === "loading") {
	await new Promise((resolve) => document.addEventListener("DOMContentLoaded", resolve, { once: true }));
}

// If working with live data, clear storage to reset app state
logDataMode("startup");
await clearStorageOnStartup(isLiveData());

// Reset inspection photos flags to default state
await restorePhotoData();

//Expose variable for generating a unique storageKey namespace per activity ID / direction
let storageKey = undefined;

// Single data structure for live and dummy data
if (app?.data?.navsData) {
	const activityId = app.data.navsData.activity_id;
	let direction = app.data.navsData.direction;

	// Store direction in state for easy access throughout app
	// NOTE: app.state.direction is a CONVENIENCE COPY of app.data.navsData.direction
	// Keeps code cleaner (app.state.direction vs app.data.navsData.direction)
	app.state.direction = titleCase(direction);

	// Use unique storage key for live data, simple key for dummy
	storageKey = `${activityId}_${direction}`;
} else {
	console.error("CRITICAL: No navsData found - app not initialized properly!");
	app.state.direction = "Return"; // Safe fallback
	storageKey = "dummyStorageKey";
}

consoleMsg(`Direction = ${app.state.direction}`);

app.state.storageKey = storageKey;
app.validator.restore(storageKey);

// Create new instances of inspection photo manager and damage photo manager classes
export const inspectionMgr = new InspectionPhotoManager("Inspection");
export const damageMgr = new DamagePhotoManager("Damage");

// Initialize app data: use live data if available, otherwise load from dummy-data-loader.js
initializeAppData();

// CLEAN UP ROBOT COMMENTS

/*
PRODUCTION SAFETY CHECK. Deliberately reads the raw flag rather than isLiveData():
isLiveData() requires navsData.apiLink, so it can never be true in exactly the
situation this check exists to catch. NAVS saying "live" while navsData is absent is
the failure we want to shout about. (The old test was `liveData !== false`, which
also fired on a plain dev page where the flag is merely undefined.)
*/
if (!app?.data?.navsData && app.settings.liveData === true) {
	console.error("CRITICAL: Production app missing navsData from API!");
	alert("Error: App data not loaded properly. Please refresh or contact support.");
}

const vendor = app.settings.vendor;

// Instantiate the page flow manager now that vendor + direction are known.
// Governs next/previous navigation and the submit-completeness set for the
// current vendor/direction flow. Vendor and direction are set once from navsData
// and never change during a given session. See src/js/classes/PageFlowManager.js.
app.flowManager = new PageFlowManager(app.settings.vendor, app.state.direction);

// New setting for toggling success / failure message on app submission
//app.settings.mockMessageType = "danger";
app.settings.mockMessageType = "success"; // Default to success for testing

// Define and initialize ending mileage field if not already set
app.data.endingMileage = app.data.endingMileage || undefined;

// Add additional keys for collecting vehicle / documents data and enforce field ordering
const vehicle = app?.data?.dataTables["vehicle-info-table"];
//const vehicleDocuments = app?.data?.dataTables["vehicle-documents-table"];

function orderDetailsTables(table, order, label) {
	// Reorder vehicle-info-table entries
	const reordered = {};
	for (const key of order) {
		if (key in table) {
			reordered[key] = table[key];
		}
	}
	// Add any remaining fields (preserves unexpected API fields)
	for (const key of Object.keys(table)) {
		if (!(key in reordered)) {
			reordered[key] = table[key];
		}
	}
	app.data.dataTables[label] = reordered;
}

if (vehicle) {
	// Define field order for vehicle-info-table (ensures consistent display)
	// MD NOTE: Add vehicle color once implemented
	const fieldOrder = ["Starting Mileage", "License Plate Number", "Fleet Number", "Brand", "Model", "VIN", "Color"];

	// Ensure Model field exists - try to get from navsData if not in dataTables
	if (!vehicle["Model"] && app.data?.navsData?.model) {
		vehicle["Model"] = app.data.navsData.model;
	}

	// Initialize required fields with defaults
	vehicle["Starting Mileage"] = vehicle["Starting Mileage"] || undefined;
	orderDetailsTables(vehicle, fieldOrder, "vehicle-info-table");
}

// If APP_BASE set, use Trevor's data
if (typeof window.APP_BASE !== "undefined") {
	app.settings.base = window.APP_BASE;
} else {
	// Default when running locally (root-based)
	app.settings.base = "";
}

defineCustomElements(window);

const headerTitle = document.getElementById("headerTitle");
const direction = app.state.direction;

// Dynamically set page's title bar text
headerTitle.innerHTML = `${vendor} ${direction} Inspection`;

// Define variables for route branching based on vendor
let previousRoute = undefined;
let nextRoute = undefined;

// Store passed vehicle information data to Ionic storage (replace with API call in prod)
await storeTableData(app.data.dataTables);

const inspector = app?.data?.dataTables["loan-info-table"]["Assigned Inspector"];
//setInspector(inspector);

updateFavicon();

const router = document.querySelector("ion-router");
consoleMsg("Router ready?", router && typeof router.componentOnReady === "function");
await router.componentOnReady();
const lastRoute = sessionStorage.getItem("lastRoute");
router.push("/", "root");

await loadFirstPage(`${app.settings.base}/pages/details.html`, "details");
consoleMsg(`LOADING FIRST PAGE: BASE PATH = ${app.settings.base}`);

// MD NOTE: Doesn't skeleton implementation go away now that all Inspection Details elements in source file?

// Swap skeleton -> real logo only after the image actually loads
const logo = document.getElementById("logo");
const logoSkeleton = document.getElementById("logo-skeleton");

// Add page skeletons while loading first page
if (logo && logoSkeleton) {
	const src = logo.getAttribute("data-src");
	if (src) {
		// If already loaded from cache, the 'load' event may fire sync after setting src.
		const reveal = () => {
			logo.style.visibility = "visible"; // show real pixels
			logoSkeleton.classList.add("hidden"); // hide skeleton
		};
		logo.addEventListener("load", reveal, { once: true }); // once: true removes event listener after initial page load
		logo.setAttribute("src", src);

		// If it’s already in the HTTP cache and painted, img 'complete' property will be true.
		if (logo.complete) reveal();
	}
}

// ---- Offline banner: no-flash + optional reserved height ----
(() => {
	const offlineDiv = document.getElementById("offline-div");
	if (!offlineDiv) return;

	const apply = () => {
		if (navigator.onLine) {
			// Hide completely
			offlineDiv.style.visibility = "hidden";
			offlineDiv.setAttribute("hidden", "");
		} else {
			// Show without flicker
			offlineDiv.removeAttribute("hidden");
			// next frame to avoid style thrash
			requestAnimationFrame(() => {
				offlineDiv.style.visibility = "visible";
			});
		}
	};

	window.addEventListener("online", apply);
	window.addEventListener("offline", apply);
	apply(); // set initial state immediately
})();

// Listen for changes in the color scheme preference
window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", updateFavicon);

/* CUSTOM ELEMENTS DEFINITION SECTION
Define all custom elements up-front rather than dynamically defining them based on a criteria 
such as vendor.*/

// DEFAULT CUSTOM ELEMENTS
customElements.define("inspection-details", Details);
customElements.define("inspection-photos", Photos);
customElements.define("exterior-inspection", Exterior);
customElements.define("interior-inspection", MbInterior);
customElements.define("glovebox-check", Glovebox);
customElements.define("interior-check", Interior);
customElements.define("road-test", RoadTest);
customElements.define("lights-check", Lights);
customElements.define("fluid-inspection", Fluids);
customElements.define("tire-inspection", Tires);
customElements.define("app-signature", Signature);
customElements.define("app-settings", AppSettings);
customElements.define("submission-complete", SubmissionComplete);

// MERCEDEDS CUSTOM ELEMENTS
customElements.define("mercedes-road-test", MbRoad);
customElements.define("mercedes-communication-check", MbCommunication);
customElements.define("mercedes-function-check", MbFunctions);

// MERCEDES DELIVERY (OUTGOING) INSPECTION CUSTOM ELEMENTS
customElements.define("mercedes-out-display", MbOutDisplay);
customElements.define("mercedes-out-vehicle-settings", MbOutVehicleSettings);
customElements.define("mercedes-out-upon-delivery", MbOutUponDelivery);

// Load content from HTML templates
await loadPages(inspectionPages);

router.push(lastRoute || "/", "root");

// Build the navigation menu once. The flow (and thus the menu) is fixed for the
// whole session, and the #menu container persists across page navigation, so
// there is no need to rebuild it on every page enter.
setMenu();
